import React, { PropsWithChildren, useCallback, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCheckPermission } from 'cityads-v3/hooks/permissions'
import { useDialogActions } from 'cityads-v3/hooks/dialog'
import { useSnackbar } from 'cityads-v3/hooks/snackbar'
import classes from './BannerRotatorButton.scss'

import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Drawer from '@mui/material/Drawer'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Snackbar from '@mui/material/Snackbar';

import BannerRotatorCreate from 'cityads-v3/containers/webmaster/tools/bannerRotator/Create'
import BannerRotatorList, { BannerRotatorListProps } from 'cityads-v3/containers/webmaster/tools/bannerRotator/List'

enum AccordionCode {
  create,
  list
}

type BannerRotatorButtonProps = PropsWithChildren<{
  className?: string,
}> & BannerRotatorListProps

export default function BannerRotatorButton({
  children,
  className,
  offerId,
}: BannerRotatorButtonProps): JSX.Element {
  const { t } = useTranslation(['BannerRotator', 'main'])
  const checkPermission = useCheckPermission()
  const [open, handleOpen, handleClose] = useDialogActions()
  const [activePanel, setActivePanel] = useState<AccordionCode | null>(AccordionCode.list)
  const { handleOpen: handleOpenPermissionSnackbar, ...permissionSnackbar } = useSnackbar({
    message: t('main:youHaveNoRights'),
    severity: 'error'
  })

  const handleSetActivePanel = useCallback((value: AccordionCode) => (event: unknown, isExpanded: boolean) => {
    setActivePanel(isExpanded ? value : null)
  }, [setActivePanel])

  const [reloadList, forceReloadList] = useReducer(r => r + 1, 0)

  return (
    <>
      <button
        className={className}
        onClick={checkPermission('show_banner_rotator')
          ? handleOpen
          : handleOpenPermissionSnackbar
        }
      >
        {children}
      </button>

      <Snackbar
        {...permissionSnackbar}
      />

      <Drawer
        anchor="right"
        classes={{ paper: classes.drawer }}
        open={open}
        onClose={handleClose}
      >
        <div>
          {checkPermission('edit_banner_rotator') && (
            <Accordion expanded={activePanel === AccordionCode.create} onChange={handleSetActivePanel(AccordionCode.create)}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                {t('createWithOffer')}
              </AccordionSummary>
              <AccordionDetails
                classes={{ root: classes.accordionDetails }}
              >
                <div className={classes.createContainer}>
                  <BannerRotatorCreate
                    offerId={offerId}
                    onSuccess={forceReloadList}
                  />
                </div>
              </AccordionDetails>
            </Accordion>
          )}

          <Accordion expanded={activePanel === AccordionCode.list} onChange={handleSetActivePanel(AccordionCode.list)}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
            >
              {t('rotatorsWithOffer')}
            </AccordionSummary>
            <AccordionDetails
              classes={{ root: classes.accordionDetails }}
            >
              <BannerRotatorList
                offerId={offerId}
                reloadTrigger={reloadList}
              />
            </AccordionDetails>
          </Accordion>

        </div>
      </Drawer>
    </>
  )
}
